import React, { useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PrivateRoute from 'routing/private-route';
import ProtectedRoute from 'routing/protected-route';
import { internalRedirect } from 'helpers';

// ================== Public views ================== //
import UsPricing from 'products/nomad_insurance/views/UsPricing';
import HospitalSearch from 'products/nomad_insurance/views/HospitalSearch';
import NotFoundPage from 'products/nomad_insurance/views/NotFoundPage';
import Policy from 'products/nomad_insurance/views/Policy';

// ================== User Views ================== //
import Profile from 'products/nomad_insurance/views/Profile';
import Purchase from 'products/nomad_insurance/views/Purchase';
import ClaimsForm from 'products/nomad_insurance/views/ClaimsForm';
import ProfileClaims from 'products/nomad_insurance/views/Profile/Claims';
import NPS from 'products/global/views/NPS';

import { fetchReferralLink } from 'products/nomad_insurance/state/referrals';
import useReferralToken from '../hooks/useReferralToken';

const Routing = () => {
  const dispatch = useDispatch();
  useReferralToken();

  useEffect(() => {
    // referral link is needed on every page under NI
    // since it determines whether to show the link to referral in navigation
    dispatch(fetchReferralLink());
  }, []);

  // TODO: Remove this after the migration to safetywing-member is complete
  const isLegacyStaging = window.location.hostname.includes('legacy.test-bird.one');

  if (!isLegacyStaging) {
    return (
      <Switch>
        {/* Internal redirects to safetywing-member */}
        <Route
          exact
          path="/nomad-insurance"
          render={() => internalRedirect('/nomad-insurance')}
        />
        <Route
          path="/nomad-insurance/faq"
          render={() => internalRedirect('/nomad-insurance/faq')}
        />
        <Route path="/nomad-insurance/login" render={() => internalRedirect('/login')} />
        <Route
          path="/nomad-insurance/signup"
          render={() => internalRedirect('/signup?product=nomad-insurance')}
        />
        <Route
          path="/nomad-insurance/terms-and-conditions"
          render={() => internalRedirect('/terms-and-conditions')}
        />
        <Route
          path="/nomad-insurance/registration"
          render={() => internalRedirect('/registration/nomad-insurance/basic-info')}
        />
        <ProtectedRoute
          exact
          path="/nomad-insurance/profile/claims"
          component={ProfileClaims}
        />
        <Route
          path="/nomad-insurance/profile"
          render={(routeProps) =>
            internalRedirect('/dashboard/nomad-insurance', routeProps)
          }
        />

        {/* Public routes */}
        <ProtectedRoute
          product="nomad_insurance"
          exact
          path="/nomad-insurance/us-pricing"
          component={UsPricing}
        />
        <PrivateRoute
          product="nomad_insurance"
          exact
          path="/nomad-insurance/hospital-search"
          component={HospitalSearch}
        />
        <ProtectedRoute
          product="nomad_insurance"
          exact
          path="/nomad-insurance/policy"
          component={Policy}
        />

        {/* User routes */}
        <PrivateRoute
          product="nomad_insurance"
          path="/nomad-insurance/claim"
          component={ClaimsForm}
        />
        <PrivateRoute
          product="nomad_insurance"
          path="/nomad-insurance/purchase-insurance"
          component={Purchase}
        />
        <PrivateRoute
          product="nomad_insurance"
          path="/nomad-insurance/renew-insurance"
          component={Purchase}
        />
        <PrivateRoute
          product="nomad_insurance"
          exact
          path="/nomad-insurance/nps"
          component={NPS}
          renderProps={{
            product: 'NOMAD_INSURANCE',
            productTitle: 'Nomad Insurance',
            profilePath: '/profile'
          }}
        />
        <PrivateRoute
          product="nomad_insurance"
          exact
          path="/nomad-insurance/claims-feedback"
          component={NPS}
          renderProps={{
            product: 'NOMAD_INSURANCE',
            productTitle: 'Nomad Insurance',
            profilePath: '/profile'
          }}
        />

        {/* Not found */}
        <Route path="/nomad-insurance/*" component={NotFoundPage} />
        <Route path="/nomads/*" component={NotFoundPage} />
      </Switch>
    );
  }

  return (
    <Switch>
      {/* Public routes */}
      <ProtectedRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/us-pricing"
        component={UsPricing}
      />
      <PrivateRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/hospital-search"
        component={HospitalSearch}
      />
      <ProtectedRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/policy"
        component={Policy}
      />

      {/* User routes */}
      <PrivateRoute
        product="nomad_insurance"
        path="/nomad-insurance/claim"
        component={ClaimsForm}
      />
      <PrivateRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/profile/claims"
        component={Profile}
      />
      <PrivateRoute
        product="nomad_insurance"
        path="/nomad-insurance/purchase-insurance"
        component={Purchase}
      />
      <PrivateRoute
        product="nomad_insurance"
        path="/nomad-insurance/renew-insurance"
        component={Purchase}
      />
      <PrivateRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/nps"
        component={NPS}
        renderProps={{
          product: 'NOMAD_INSURANCE',
          productTitle: 'Nomad Insurance',
          profilePath: '/profile'
        }}
      />
      <PrivateRoute
        product="nomad_insurance"
        exact
        path="/nomad-insurance/claims-feedback"
        component={NPS}
        renderProps={{
          product: 'NOMAD_INSURANCE',
          productTitle: 'Nomad Insurance',
          profilePath: '/profile'
        }}
      />

      <Redirect from="/nomad-insurance/pricing" to="/nomad-insurance#pricing" />

      {/* Not found */}
      <Route path="/nomad-insurance/*" component={NotFoundPage} />
      <Route path="/nomads/*" component={NotFoundPage} />
    </Switch>
  );
};

export default Routing;
